import { useState, useEffect, useRef } from 'react'
import { findExercisesByVideoId } from '../services/exerciseService'
import { parseYouTubeTitle, generateAutoTags, getHorizonsTagsForCategory, getDecadeTag, getEraTagForExercise, HORIZONS_MUSIC_CATEGORIES, HORIZONS_STYLE_ORDER } from '../utils/tagGenerator'
import { getMusicalEra, getMusicalErasForCategory } from '../data/musicalEras'
import { formatExerciseTitle } from '../utils/exerciseTitleFormat'
import { parseComposerToGivenAndFamily } from '../utils/exerciseDisplay'
import { getComposerGivenName } from '../data/composerGivenNames'
import { computeDifficultyFromContent, getDisplayDifficulty } from '../utils/difficultyFromContent'
import { filterKnownTags } from '../data/knownTags'
import {
  CLASSICAL_FORMATIONS,
  HORIZONS_FORMATIONS,
  CLASSICAL_GENRES,
  CHAMBER_ENSEMBLE_GENRE_IDS,
  getFormationTag,
  getGenreTag,
  inferGenreFromWorkTitle,
  mergeFormationIdsFromReferenceAndTitle
} from '../data/formations'
import { FormationInstrumentPicker } from './FormationInstrumentPicker'
import { findMatchingWork, getKnownComposerNames } from '../services/worksReference'
import { parseTitle as parseTitleViaApi, getMovements as getMovementsViaApi, getSmartFillCache, setSmartFillCache } from '../services/opusApi'
import { useBackdropDismiss } from '../hooks/useBackdropDismiss'
import './SaveExerciseModal.css'

function SaveExerciseModal({ 
  isOpen, 
  onClose, 
  onSave, 
  isSaving,
  videoId,
  videoTitle,
  getVideoTitle,
  markers,
  chordData,
  isEditMode = false,
  initialAutoTags = null,
  initialSection = null,
  initialMusicCategory = null,
  initialFormation = null,
  initialGenre = null,
  initialComposer = null,
  initialComposerGivenName = null,
  initialComposerFamilyName = null,
  initialWorkTitle = null,
  initialMovementTitle = null,
  initialExerciseTitle = null,
  initialDifficulty = null,
  initialYear = null,
  initialMusicalEra = null,
  initialSubtitle = null,
  isAdminTeacher = false,
  initialPublicPreview = false,
}) {
  const [composerGivenName, setComposerGivenName] = useState('')
  const [composerFamilyName, setComposerFamilyName] = useState('')
  const [workTitle, setWorkTitle] = useState('')
  const [movementTitle, setMovementTitle] = useState('')
  const [exerciseTitle, setExerciseTitle] = useState('')
  const [subtitle, setSubtitle] = useState('')
  const [difficulty, setDifficulty] = useState('')
  const [privacy, setPrivacy] = useState('private')
  const [exerciseType, setExerciseType] = useState('classical')
  const [musicCategory, setMusicCategory] = useState('')
  const [publicationYear, setPublicationYear] = useState('')
  const [musicalEra, setMusicalEra] = useState('') // id d'ère musicale (ex: 'synthpop-new-wave')
  const [selectedFormations, setSelectedFormations] = useState([]) // instrumentation (multi-select)
  const [genre, setGenre] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [existingExercisesCount, setExistingExercisesCount] = useState(0)
  const [referenceMovementOptions, setReferenceMovementOptions] = useState(null) // liste de mouvements depuis la base œuvres (si œuvre trouvée)
  const [workMatchStatus, setWorkMatchStatus] = useState(null) // { status: 'verified' | 'unverified' | null, score: number | null }
  const [imslpLinkFound, setImslpLinkFound] = useState(null) // true/false après appel API mouvements, null si pas encore connu
  const [imslpUrlUsed, setImslpUrlUsed] = useState(null) // URL IMSLP utilisée si trouvée
  const [isReanalyzing, setIsReanalyzing] = useState(false) // re-analyse du titre en ignorant le cache serveur
  const [manualTags, setManualTags] = useState([])
  const [newTagInput, setNewTagInput] = useState('')
  const [tagSuggestions, setTagSuggestions] = useState([])
  const [showTagSuggestions, setShowTagSuggestions] = useState(false)
  const [publicPreviewTeaser, setPublicPreviewTeaser] = useState(false)
  const tagInputRef = useRef(null)

  const composerDisplay = [composerGivenName, composerFamilyName].filter(Boolean).join(' ').trim()

  // Pré-remplissage intelligent à l'ouverture (videoTitle en deps pour relancer quand le titre arrive après ouverture)
  useEffect(() => {
    if (isOpen && videoId) {
      setIsLoading(true)
      setImslpLinkFound(null)
      setImslpUrlUsed(null)
      loadSmartFill()
    }
  }, [isOpen, videoId, videoTitle])

  // Initialiser type / style / année à l'ouverture
  useEffect(() => {
    if (!isOpen) return
    if (isEditMode && (initialSection === 'horizons' || initialMusicCategory)) {
      setExerciseType('horizons')
      setMusicCategory(initialMusicCategory || '')
      setPublicationYear(initialYear != null ? String(initialYear) : '')
      setMusicalEra(initialMusicalEra || '')
    } else if (!isEditMode) {
      setExerciseType('classical')
      setMusicCategory('')
      setPublicationYear('')
      setMusicalEra('')
    }
  }, [isOpen, isEditMode, initialSection, initialMusicCategory, initialYear, initialMusicalEra])

  useEffect(() => {
    if (!isOpen) return
    if (isAdminTeacher) {
      setPublicPreviewTeaser(!!initialPublicPreview)
    } else {
      setPublicPreviewTeaser(false)
    }
  }, [isOpen, isAdminTeacher, initialPublicPreview])

  // Initialiser formation (array) et genre à l'ouverture (édition)
  useEffect(() => {
    if (!isOpen) return
    const formationArray = Array.isArray(initialFormation) ? initialFormation : (initialFormation ? [initialFormation] : [])
    setSelectedFormations(formationArray)
    setGenre(initialGenre || '')
  }, [isOpen, initialFormation, initialGenre])

  // Réinitialiser formations si elles n'existent pas dans la liste du type actuel
  useEffect(() => {
    const list = exerciseType === 'horizons' ? HORIZONS_FORMATIONS : CLASSICAL_FORMATIONS
    const ids = new Set(list.map(f => f.id))
    setSelectedFormations(prev => prev.filter(id => ids.has(id)))
  }, [exerciseType])

  // Auto-suggestion de l'ère musicale quand année ou catégorie changent
  useEffect(() => {
    if (!isOpen || exerciseType !== 'horizons' || !musicCategory || !publicationYear) {
      if (musicalEra && exerciseType !== 'horizons') setMusicalEra('')
      return
    }
    const era = getMusicalEra(musicCategory, publicationYear)
    if (era) {
      setMusicalEra(era.id)
    } else {
      setMusicalEra('')
    }
  }, [isOpen, exerciseType, musicCategory, publicationYear])

  // Suggestion formation + genre depuis workTitle (création) — corrige autre + genre chambre, ou réf. œuvre incomplète
  useEffect(() => {
    if (!isOpen || isEditMode) return
    const list = exerciseType === 'horizons' ? HORIZONS_FORMATIONS : CLASSICAL_FORMATIONS
    const formationIdsSet = new Set(list.map((f) => f.id))
    const section = exerciseType === 'horizons' ? 'horizons' : null
    const genreResolved = genre || (exerciseType === 'classical' ? inferGenreFromWorkTitle(workTitle) : null)
    const merged = mergeFormationIdsFromReferenceAndTitle(
      selectedFormations,
      workTitle,
      section,
      genreResolved,
      (id) => formationIdsSet.has(id)
    )
    const onlyAutre =
      selectedFormations.length > 0 && selectedFormations.every((id) => id === 'autre' || id === 'autre_solo')
    const chamberGenreAutre =
      Boolean(genre) && CHAMBER_ENSEMBLE_GENRE_IDS.has(genre) && selectedFormations.includes('autre')

    if ((selectedFormations.length === 0 || onlyAutre || chamberGenreAutre) && merged.length > 0) {
      const prevSorted = [...selectedFormations].sort().join(',')
      const nextSorted = [...merged].sort().join(',')
      if (prevSorted !== nextSorted) setSelectedFormations(merged)
    }
    if (!genre) {
      const inferredGenre = exerciseType === 'classical' ? inferGenreFromWorkTitle(workTitle) : null
      if (inferredGenre) setGenre(inferredGenre)
    }
  }, [isOpen, isEditMode, workTitle, exerciseType, selectedFormations, genre])

  // Initialiser manualTags à l'ouverture : édition = initialAutoTags + recalcul depuis chordData actuel (pour inclure DemiCadence etc. si ajouté après chargement)
  useEffect(() => {
    if (!isOpen) return
    if (isEditMode && Array.isArray(initialAutoTags)) {
      const generated = generateAutoTags(markers || [], chordData || {}, composerDisplay.trim() || null)
      const merged = [...new Set([...initialAutoTags, ...generated])]
      setManualTags(merged)
    } else {
      const generated = generateAutoTags(markers || [], chordData || {}, composerDisplay.trim() || null)
      const horizonsTags = (initialSection === 'horizons' && initialMusicCategory) ? getHorizonsTagsForCategory(initialMusicCategory) : []
      const decadeTag = (initialSection === 'horizons' && initialYear) ? getDecadeTag(initialYear) : null
      const eraTag = (initialSection === 'horizons' && initialMusicCategory && initialYear) ? getEraTagForExercise(initialMusicCategory, initialYear) : null
      const formationTags = (Array.isArray(initialFormation) ? initialFormation : (initialFormation ? [initialFormation] : [])).map(id => getFormationTag(id)).filter(Boolean)
      const genreTag = initialGenre && getGenreTag(initialGenre) ? [getGenreTag(initialGenre)] : []
      setManualTags([...new Set([...horizonsTags, ...(decadeTag ? [decadeTag] : []), ...(eraTag ? [eraTag] : []), ...formationTags, ...genreTag, ...generated])])
    }
  }, [isOpen, isEditMode, initialAutoTags, initialSection, initialMusicCategory, initialFormation, initialGenre, initialYear, markers, chordData, composerDisplay])

  // Mettre à jour les tags générés quand composer/markers/chordData/type/category/formations/genre/era changent (création uniquement)
  useEffect(() => {
    if (!isOpen || isEditMode) return
    const generated = generateAutoTags(markers || [], chordData || {}, composerDisplay.trim() || null)
    const horizonsTags = exerciseType === 'horizons' && musicCategory ? getHorizonsTagsForCategory(musicCategory) : []
    const decadeTag = exerciseType === 'horizons' && publicationYear ? getDecadeTag(publicationYear) : null
    const eraTag = exerciseType === 'horizons' && musicCategory && publicationYear ? getEraTagForExercise(musicCategory, publicationYear) : null
    const formationTags = selectedFormations.map(id => getFormationTag(id)).filter(Boolean)
    const genreTag = genre ? getGenreTag(genre) : null
    setManualTags(prev => {
      const withoutFormationGenreDecadeEra = prev.filter(t => !t.startsWith('Formation') && !t.startsWith('Genre') && !/^\d{4}s$/.test(t) && !t.startsWith('Ere'))
      const merged = [...new Set([...horizonsTags, ...(decadeTag ? [decadeTag] : []), ...(eraTag ? [eraTag] : []), ...formationTags, ...(genreTag ? [genreTag] : []), ...generated, ...withoutFormationGenreDecadeEra])]
      if (prev.length === merged.length && merged.every(t => prev.includes(t))) return prev
      return merged
    })
  }, [isOpen, isEditMode, composerDisplay, markers, chordData, exerciseType, musicCategory, publicationYear, selectedFormations, genre, musicalEra])

  // Enrichissement depuis le fichier de référence (œuvres/mouvements) : quand compositeur + titre œuvre sont connus
  useEffect(() => {
    if (!isOpen || isLoading) return
    const c = composerDisplay.trim()
    const w = workTitle.trim()
    if (!c || !w) {
      setReferenceMovementOptions(null)
      setWorkMatchStatus(null)
      setImslpLinkFound(null)
      setImslpUrlUsed(null)
      return
    }
    let isCancelled = false
    findMatchingWork(c, w).then(async (matchResult) => {
      if (isCancelled) return
      if (matchResult) {
        setWorkMatchStatus({ status: 'verified', score: matchResult.matchScore ?? 1 })
        let opts = Array.isArray(matchResult.work.movements) ? matchResult.work.movements : []
        if (opts.length === 0 && c && w) {
          const api = await getMovementsViaApi(c, w)
          if ((api.movements ?? []).length > 0) opts = api.movements
          setImslpLinkFound(api.imslpLinkFound === true)
          setImslpUrlUsed(api.imslpUrlUsed ?? null)
        }
        setReferenceMovementOptions(opts)
      } else {
        setWorkMatchStatus({ status: 'unverified', score: null })
        if (c && w) {
          const api = await getMovementsViaApi(c, w)
          setReferenceMovementOptions((api.movements ?? []).length > 0 ? api.movements : null)
          setImslpLinkFound(api.imslpLinkFound === true)
          setImslpUrlUsed(api.imslpUrlUsed ?? null)
        } else {
          setReferenceMovementOptions(null)
          setImslpLinkFound(null)
          setImslpUrlUsed(null)
        }
      }
    })
    return () => { isCancelled = true }
  }, [isOpen, isLoading, composerDisplay, workTitle])

  // Suggestions pour l'input "ajouter un tag"
  useEffect(() => {
    if (!newTagInput.trim()) {
      setTagSuggestions(filterKnownTags(''))
      return
    }
    setTagSuggestions(filterKnownTags(newTagInput))
  }, [newTagInput])

  // Mettre à jour le titre formaté quand l'utilisateur change compositeur / œuvre / mouvement (création, après chargement)
  useEffect(() => {
    if (!isOpen || isEditMode || isLoading) return
    if (!composerDisplay.trim() && !workTitle.trim()) return
    setExerciseTitle(formatExerciseTitle(composerDisplay, workTitle, movementTitle))
  }, [isOpen, isEditMode, isLoading, composerDisplay, workTitle, movementTitle])

  const loadSmartFill = async () => {
    try {
      const existingExercises = await findExercisesByVideoId(videoId)
      if (isEditMode && (initialExerciseTitle != null || initialComposer != null || initialComposerFamilyName != null)) {
        const hasStructuredInitialComposer =
          Boolean((initialComposerGivenName ?? '').trim()) || Boolean((initialComposerFamilyName ?? '').trim())
        if (hasStructuredInitialComposer) {
          setComposerGivenName(initialComposerGivenName ?? '')
          setComposerFamilyName(initialComposerFamilyName ?? '')
        } else {
          let { givenName, familyName } = parseComposerToGivenAndFamily(initialComposer ?? '')
          if (!givenName) givenName = getComposerGivenName(initialComposer ?? '') || ''
          setComposerGivenName(givenName)
          setComposerFamilyName(familyName)
        }
        setWorkTitle(initialWorkTitle ?? '')
        setMovementTitle(initialMovementTitle ?? '')
        setExerciseTitle(initialExerciseTitle ?? 'Analyse harmonique')
        setSubtitle(initialSubtitle ?? '')
        setDifficulty((initialDifficulty != null ? getDisplayDifficulty(initialDifficulty) : null) ?? computeDifficultyFromContent(markers || [], chordData || {}) ?? '')
        setExistingExercisesCount(Math.max(0, existingExercises.length))
        return
      }

      const listFormations = exerciseType === 'horizons' ? HORIZONS_FORMATIONS : CLASSICAL_FORMATIONS
      const formationIds = new Set(listFormations.map((f) => f.id))

      if (existingExercises.length > 0) {
        const firstExercise = existingExercises[0]
        if (firstExercise.metadata) {
          const meta = firstExercise.metadata
          const loadedWorkTitle = meta.workTitle || ''
          const loadedMovement = meta.movementTitle || ''
          const hasStructuredMetaComposer =
            Boolean((meta.composerGivenName ?? '').trim()) || Boolean((meta.composerFamilyName ?? '').trim())
          if (hasStructuredMetaComposer) {
            setComposerGivenName(meta.composerGivenName ?? '')
            setComposerFamilyName(meta.composerFamilyName ?? '')
          } else {
            let { givenName, familyName } = parseComposerToGivenAndFamily(meta.composer || '')
            if (!givenName) givenName = getComposerGivenName(meta.composer || '') || ''
            setComposerGivenName(givenName)
            setComposerFamilyName(familyName)
          }
          setWorkTitle(loadedWorkTitle)
          setMovementTitle(loadedMovement)
          setExistingExercisesCount(existingExercises.length)
          const computedDifficulty = computeDifficultyFromContent(markers || [], chordData || {})
          setDifficulty(computedDifficulty ?? getDisplayDifficulty(firstExercise.metadata.difficulty) ?? '')
          setExerciseTitle(formatExerciseTitle([meta.composerGivenName, meta.composerFamilyName].filter(Boolean).join(' ') || meta.composer || '', loadedWorkTitle, loadedMovement))
          const fromMeta = firstExercise.metadata.formation
          const loadedFormations = Array.isArray(fromMeta) ? fromMeta : fromMeta ? [fromMeta] : []
          const genreMeta = firstExercise.metadata.genre || null
          const mergedLoad = mergeFormationIdsFromReferenceAndTitle(
            loadedFormations,
            loadedWorkTitle,
            exerciseType === 'horizons' ? 'horizons' : null,
            genreMeta || (exerciseType === 'classical' ? inferGenreFromWorkTitle(loadedWorkTitle) : null),
            (id) => formationIds.has(id)
          )
          if (mergedLoad.length > 0) setSelectedFormations(mergedLoad)
          if (genreMeta) setGenre(genreMeta)
          else {
            const inferredGenre = exerciseType === 'classical' ? inferGenreFromWorkTitle(loadedWorkTitle) : null
            if (inferredGenre) setGenre(inferredGenre)
          }
        } else {
          setExistingExercisesCount(existingExercises.length)
        }
      } else {
        // Cache client : si déjà rempli pour cette vidéo, pas d’appel API
        const cached = getSmartFillCache(videoId)
        let parsedComposer = ''
        let parsedWorkTitle = ''
        let parsedMovement = null
        let movementOptions = []

        if (cached?.parsed) {
          parsedComposer = cached.parsed.composer || ''
          parsedWorkTitle = cached.parsed.workTitle || ''
          parsedMovement = cached.parsed.movementTitle || null
          movementOptions = Array.isArray(cached.movements) ? cached.movements : []
          setImslpLinkFound(cached.imslpLinkFound === true)
          setImslpUrlUsed(cached.imslpUrlUsed ?? null)
        } else {
          let titleToParse = videoTitle
          if ((!titleToParse || titleToParse.trim() === '' || titleToParse === 'Vidéo YouTube') && typeof getVideoTitle === 'function') {
            const fromPlayer = getVideoTitle()
            if (fromPlayer) titleToParse = fromPlayer
          }
          const knownComposerNames = await getKnownComposerNames()
          let parsed = null
          try {
            parsed = await parseTitleViaApi(titleToParse, videoId)
          } catch (_) { /* ignore */ }
          if (!parsed || (parsed && !parsed.composer && !parsed.workTitle)) {
            parsed = parseYouTubeTitle(titleToParse, knownComposerNames)
          }
          parsedComposer = parsed?.composer || ''
          parsedWorkTitle = parsed?.workTitle || ''
          parsedMovement = parsed?.movementTitle ?? null
          const matchResult = await findMatchingWork(parsedComposer, parsedWorkTitle)
          movementOptions = matchResult?.work?.movements || []
          let imslpFromApi = false
          let imslpUrlFromApi = null
          if (movementOptions.length === 0 && parsedComposer && parsedWorkTitle) {
            const catalogOpt = parsed?.catalog?.trim() ? { catalog: parsed.catalog } : {}
            const api = await getMovementsViaApi(parsedComposer, parsedWorkTitle, catalogOpt)
            if ((api.movements ?? []).length > 0) movementOptions = api.movements
            setImslpLinkFound(api.imslpLinkFound === true)
            setImslpUrlUsed(api.imslpUrlUsed ?? null)
            imslpFromApi = api.imslpLinkFound === true
            imslpUrlFromApi = api.imslpUrlUsed ?? null
          }
          setSmartFillCache(videoId, {
            parsed: {
              composer: parsedComposer,
              workTitle: parsedWorkTitle,
              movementTitle: parsedMovement || '',
              catalog: parsed?.catalog ?? '',
              key: parsed?.key ?? '',
            },
            movements: movementOptions,
            imslpLinkFound: imslpFromApi,
            imslpUrlUsed: imslpUrlFromApi,
          })
        }

        const matchResult = await findMatchingWork(parsedComposer, parsedWorkTitle)
        if (matchResult) {
          let { givenName: g, familyName: f } = parseComposerToGivenAndFamily(matchResult.composerName)
          if (!g) g = getComposerGivenName(matchResult.composerName) || ''
          setComposerGivenName(g)
          setComposerFamilyName(f)
          setWorkTitle(matchResult.work.workTitle)
          setMovementTitle(parsedMovement || '')
          setReferenceMovementOptions(movementOptions.length > 0 ? movementOptions : (matchResult.work.movements || []))
          setWorkMatchStatus({ status: 'verified', score: matchResult.matchScore ?? 1 })
          setExerciseTitle(formatExerciseTitle(matchResult.composerName, matchResult.work.workTitle, parsedMovement || ''))
          const work = matchResult.work
          const genreFromRef = work.genreId && CLASSICAL_GENRES.some((g) => g.id === work.genreId) ? work.genreId : null
          const genreResolved =
            exerciseType === 'classical' ? (genreFromRef || inferGenreFromWorkTitle(work.workTitle)) : null
          const mergedFormations = mergeFormationIdsFromReferenceAndTitle(
            work.formationIds || [],
            work.workTitle,
            exerciseType === 'horizons' ? 'horizons' : null,
            genreResolved,
            (id) => formationIds.has(id)
          )
          if (mergedFormations.length > 0) setSelectedFormations(mergedFormations)
          if (genreResolved) setGenre(genreResolved)
        } else {
          let { givenName: g, familyName: f } = parseComposerToGivenAndFamily(parsedComposer)
          if (!g) g = getComposerGivenName(parsedComposer) || ''
          setComposerGivenName(g)
          setComposerFamilyName(f)
          setWorkTitle(parsedWorkTitle)
          setMovementTitle(parsedMovement || '')
          setReferenceMovementOptions(movementOptions)
          setWorkMatchStatus({ status: 'unverified', score: null })
          setExistingExercisesCount(0)
          setExerciseTitle(formatExerciseTitle(parsedComposer, parsedWorkTitle, parsedMovement || ''))
          const inferredGenre = exerciseType === 'classical' ? inferGenreFromWorkTitle(parsedWorkTitle) : null
          const mergedFormations = mergeFormationIdsFromReferenceAndTitle(
            [],
            parsedWorkTitle,
            exerciseType === 'horizons' ? 'horizons' : null,
            inferredGenre,
            (id) => formationIds.has(id)
          )
          if (mergedFormations.length > 0) setSelectedFormations(mergedFormations)
          if (inferredGenre) setGenre(inferredGenre)
        }
        setExistingExercisesCount(0)
        const computedDifficulty = computeDifficultyFromContent(markers || [], chordData || {})
        setDifficulty(computedDifficulty ?? '')
      }
    } catch (error) {
      console.error('Erreur lors du pré-remplissage:', error)
      let titleToParse = videoTitle
      if ((!titleToParse || titleToParse.trim() === '' || titleToParse === 'Vidéo YouTube') && typeof getVideoTitle === 'function') {
        const fromPlayer = getVideoTitle()
        if (fromPlayer) titleToParse = fromPlayer
      }
      const knownComposerNames = await getKnownComposerNames()
      const parsed = parseYouTubeTitle(titleToParse, knownComposerNames)
      const parsedComposer = parsed.composer || ''
      const parsedWorkTitle = parsed.workTitle || ''
      const parsedMovement = parsed.movementTitle || null
      const matchResult = await findMatchingWork(parsedComposer, parsedWorkTitle)
      const listFormationsFallback = exerciseType === 'horizons' ? HORIZONS_FORMATIONS : CLASSICAL_FORMATIONS
      const formationIdsFallback = new Set(listFormationsFallback.map((f) => f.id))
      if (matchResult) {
        let { givenName: g, familyName: f } = parseComposerToGivenAndFamily(matchResult.composerName)
        if (!g) g = getComposerGivenName(matchResult.composerName) || ''
        setComposerGivenName(g)
        setComposerFamilyName(f)
        setWorkTitle(matchResult.work.workTitle)
        setMovementTitle(parsedMovement || '')
        setReferenceMovementOptions(matchResult.work.movements || [])
        setWorkMatchStatus({ status: 'verified', score: matchResult.matchScore ?? 1 })
        setExerciseTitle(formatExerciseTitle(matchResult.composerName, matchResult.work.workTitle, parsedMovement || ''))
        const work = matchResult.work
        const genreFromRef = work.genreId && CLASSICAL_GENRES.some((g) => g.id === work.genreId) ? work.genreId : null
        const genreResolved =
          exerciseType === 'classical' ? (genreFromRef || inferGenreFromWorkTitle(work.workTitle)) : null
        const mergedFormations = mergeFormationIdsFromReferenceAndTitle(
          work.formationIds || [],
          work.workTitle,
          exerciseType === 'horizons' ? 'horizons' : null,
          genreResolved,
          (id) => formationIdsFallback.has(id)
        )
        if (mergedFormations.length > 0) setSelectedFormations(mergedFormations)
        if (genreResolved) setGenre(genreResolved)
      } else {
        let { givenName: g, familyName: f } = parseComposerToGivenAndFamily(parsedComposer)
        if (!g) g = getComposerGivenName(parsedComposer) || ''
        setComposerGivenName(g)
        setComposerFamilyName(f)
        setWorkTitle(parsedWorkTitle)
        setMovementTitle(parsedMovement || '')
        setReferenceMovementOptions([])
        setWorkMatchStatus({ status: 'unverified', score: null })
        setExerciseTitle(formatExerciseTitle(parsedComposer, parsedWorkTitle, parsedMovement || ''))
        const inferredGenre = exerciseType === 'classical' ? inferGenreFromWorkTitle(parsedWorkTitle) : null
        const mergedFormations = mergeFormationIdsFromReferenceAndTitle(
          [],
          parsedWorkTitle,
          exerciseType === 'horizons' ? 'horizons' : null,
          inferredGenre,
          (id) => formationIdsFallback.has(id)
        )
        if (mergedFormations.length > 0) setSelectedFormations(mergedFormations)
        if (inferredGenre) setGenre(inferredGenre)
      }
      setExistingExercisesCount(0)
      const computedDifficulty = computeDifficultyFromContent(markers || [], chordData || {})
      setDifficulty(computedDifficulty ?? '')
    } finally {
      setIsLoading(false)
    }
  }

  /** Re-analyser le titre avec Gemini en ignorant le cache serveur (utile si une ancienne réponse était incorrecte). */
  const handleReanalyzeTitle = async () => {
    if (!videoId || String(videoId).length !== 11 || isReanalyzing || isSaving) return
    let titleToParse = videoTitle
    if ((!titleToParse || titleToParse.trim() === '' || titleToParse === 'Vidéo YouTube') && typeof getVideoTitle === 'function') {
      const fromPlayer = getVideoTitle()
      if (fromPlayer) titleToParse = fromPlayer
    }
    if (!titleToParse?.trim()) return
    setIsReanalyzing(true)
    try {
      const knownComposerNames = await getKnownComposerNames()
      let parsed = null
      try {
        parsed = await parseTitleViaApi(titleToParse, videoId, { skipCache: true })
      } catch (_) { /* ignore */ }
      if (!parsed || (!parsed.composer && !parsed.workTitle)) {
        parsed = parseYouTubeTitle(titleToParse, knownComposerNames)
      }
      const parsedComposer = parsed?.composer || ''
      const parsedWorkTitle = parsed?.workTitle || ''
      const parsedMovement = parsed?.movementTitle ?? null
      const listFormations = exerciseType === 'horizons' ? HORIZONS_FORMATIONS : CLASSICAL_FORMATIONS
      const formationIds = new Set(listFormations.map((f) => f.id))
      const matchResult = await findMatchingWork(parsedComposer, parsedWorkTitle)
      let movementOptions = matchResult?.work?.movements || []
      let imslpFromApi = false
      let imslpUrlFromApi = null
      if (movementOptions.length === 0 && parsedComposer && parsedWorkTitle) {
        const catalogOpt = parsed?.catalog?.trim() ? { catalog: parsed.catalog } : {}
        const api = await getMovementsViaApi(parsedComposer, parsedWorkTitle, catalogOpt)
        if ((api.movements ?? []).length > 0) movementOptions = api.movements
        setImslpLinkFound(api.imslpLinkFound === true)
        setImslpUrlUsed(api.imslpUrlUsed ?? null)
        imslpFromApi = api.imslpLinkFound === true
        imslpUrlFromApi = api.imslpUrlUsed ?? null
      }
      setSmartFillCache(videoId, {
        parsed: {
          composer: parsedComposer,
          workTitle: parsedWorkTitle,
          movementTitle: parsedMovement || '',
          catalog: parsed?.catalog ?? '',
          key: parsed?.key ?? '',
        },
        movements: movementOptions,
        imslpLinkFound: imslpFromApi,
        imslpUrlUsed: imslpUrlFromApi,
      })
      if (matchResult) {
        let { givenName: g, familyName: f } = parseComposerToGivenAndFamily(matchResult.composerName)
        if (!g) g = getComposerGivenName(matchResult.composerName) || ''
        setComposerGivenName(g)
        setComposerFamilyName(f)
        setWorkTitle(matchResult.work.workTitle)
        setMovementTitle(parsedMovement || '')
        setReferenceMovementOptions(movementOptions.length > 0 ? movementOptions : (matchResult.work.movements || []))
        setWorkMatchStatus({ status: 'verified', score: matchResult.matchScore ?? 1 })
        setExerciseTitle(formatExerciseTitle(matchResult.composerName, matchResult.work.workTitle, parsedMovement || ''))
        const work = matchResult.work
        const genreFromRef = work.genreId && CLASSICAL_GENRES.some((g) => g.id === work.genreId) ? work.genreId : null
        const genreResolved =
          exerciseType === 'classical' ? (genreFromRef || inferGenreFromWorkTitle(work.workTitle)) : null
        const mergedFormations = mergeFormationIdsFromReferenceAndTitle(
          work.formationIds || [],
          work.workTitle,
          exerciseType === 'horizons' ? 'horizons' : null,
          genreResolved,
          (id) => formationIds.has(id)
        )
        if (mergedFormations.length > 0) setSelectedFormations(mergedFormations)
        if (genreResolved) setGenre(genreResolved)
      } else {
        let { givenName: g, familyName: f } = parseComposerToGivenAndFamily(parsedComposer)
        if (!g) g = getComposerGivenName(parsedComposer) || ''
        setComposerGivenName(g)
        setComposerFamilyName(f)
        setWorkTitle(parsedWorkTitle)
        setMovementTitle(parsedMovement || '')
        setReferenceMovementOptions(movementOptions)
        setWorkMatchStatus({ status: 'unverified', score: null })
        setExerciseTitle(formatExerciseTitle(parsedComposer, parsedWorkTitle, parsedMovement || ''))
        const inferredGenre = exerciseType === 'classical' ? inferGenreFromWorkTitle(parsedWorkTitle) : null
        const mergedFormations = mergeFormationIdsFromReferenceAndTitle(
          [],
          parsedWorkTitle,
          exerciseType === 'horizons' ? 'horizons' : null,
          inferredGenre,
          (id) => formationIds.has(id)
        )
        if (mergedFormations.length > 0) setSelectedFormations(mergedFormations)
        if (inferredGenre) setGenre(inferredGenre)
      }
      setExistingExercisesCount(0)
      const computedDifficulty = computeDifficultyFromContent(markers || [], chordData || {})
      setDifficulty(computedDifficulty ?? '')
    } finally {
      setIsReanalyzing(false)
    }
  }

  const handleRemoveTag = (indexToRemove) => {
    setManualTags(prev => prev.filter((_, i) => i !== indexToRemove))
  }

  const handleAddTag = (tag) => {
    const t = (typeof tag === 'string' ? tag : newTagInput).trim()
    if (!t) return
    const normalized = t.replace(/\s+/g, '')
    const value = normalized || t
    if (value && !manualTags.includes(value)) {
      setManualTags(prev => [...prev, value])
      setNewTagInput('')
      setShowTagSuggestions(false)
    }
  }

  const handleResetAutoTags = () => {
    const generated = generateAutoTags(markers || [], chordData || {}, composerDisplay.trim() || null)
    const horizonsTags = exerciseType === 'horizons' && musicCategory ? getHorizonsTagsForCategory(musicCategory) : []
    const decadeTag = exerciseType === 'horizons' && publicationYear ? getDecadeTag(publicationYear) : null
    const eraTag = exerciseType === 'horizons' && musicCategory && publicationYear ? getEraTagForExercise(musicCategory, publicationYear) : null
    const formationTags = selectedFormations.map(id => getFormationTag(id)).filter(Boolean)
    const genreTag = genre ? getGenreTag(genre) : null
    setManualTags([...new Set([...horizonsTags, ...(decadeTag ? [decadeTag] : []), ...(eraTag ? [eraTag] : []), ...formationTags, ...(genreTag ? [genreTag] : []), ...generated])])
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (exerciseTitle.trim()) {
      const section = exerciseType === 'horizons' ? 'horizons' : null
      const category = exerciseType === 'horizons' && musicCategory ? musicCategory : null
      const formationTags = selectedFormations.map(id => getFormationTag(id)).filter(Boolean)
      const genreTag = genre ? getGenreTag(genre) : null
      const tagsWithoutFormationGenre = manualTags.filter(t => !t.startsWith('Formation') && !t.startsWith('Genre'))
      const autoTags = [...new Set([...formationTags, ...(genreTag ? [genreTag] : []), ...tagsWithoutFormationGenre])]
      const yearValue = exerciseType === 'horizons' && publicationYear ? Number(publicationYear) : null
      const eraValue = exerciseType === 'horizons' && musicalEra ? musicalEra : null
      onSave({
        composer: composerDisplay.trim() || null,
        composerGivenName: composerGivenName.trim() || null,
        composerFamilyName: composerFamilyName.trim() || null,
        workTitle: workTitle.trim() || null,
        movementTitle: movementTitle.trim() || null,
        exerciseTitle: exerciseTitle.trim(),
        subtitle: subtitle.trim() || null,
        difficulty: difficulty.trim() || null,
        privacy: privacy,
        autoTags,
        section,
        musicCategory: category,
        formation: selectedFormations.length > 0 ? selectedFormations : null,
        genre: genre || null,
        year: yearValue,
        musicalEra: eraValue,
        ...(isAdminTeacher ? { publicPreview: publicPreviewTeaser } : {}),
      })
      resetForm()
    }
  }

  const resetForm = () => {
    setComposerGivenName('')
    setComposerFamilyName('')
    setWorkTitle('')
    setMovementTitle('')
    setExerciseTitle('')
    setSubtitle('')
    setDifficulty('')
    setPrivacy('private')
    setExerciseType('classical')
    setMusicCategory('')
    setPublicationYear('')
    setMusicalEra('')
    setSelectedFormations([])
    setGenre('')
    setExistingExercisesCount(0)
    setReferenceMovementOptions(null)
    setWorkMatchStatus(null)
    setManualTags([])
    setNewTagInput('')
    setShowTagSuggestions(false)
  }

  const handleClose = () => {
    if (!isSaving) {
      resetForm()
      onClose()
    }
  }

  const backdropDismiss = useBackdropDismiss(handleClose)

  if (!isOpen) return null

  return (
    <div className="save-modal-backdrop" {...backdropDismiss}>
      <div className="save-modal-container" onClick={(e) => e.stopPropagation()}>
        <div className="save-modal-header">
          <h2>{isEditMode ? 'Sauvegarder les modifications' : 'Sauvegarder l\'exercice'}</h2>
          <button 
            className="save-modal-close" 
            onClick={handleClose}
            disabled={isSaving}
          >
            ×
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="save-modal-form">
          <div className="save-modal-body">
          {isLoading && (
            <div className="save-modal-loading">
              <span>Chargement des suggestions...</span>
            </div>
          )}

          {!isLoading && existingExercisesCount > 0 && !isEditMode && (
            <div className="save-modal-existing-hint" role="status">
              {existingExercisesCount} exercice{existingExercisesCount > 1 ? 's' : ''} existant{existingExercisesCount > 1 ? 's' : ''} pour cette œuvre. Vous pouvez ajouter un sous-titre pour différencier cet extrait.
            </div>
          )}

          <div className="save-modal-field save-modal-composer-fields">
            <label htmlFor="exercise-composer-given">Prénom du compositeur</label>
            <input
              id="exercise-composer-given"
              type="text"
              value={composerGivenName}
              onChange={(e) => setComposerGivenName(e.target.value)}
              placeholder="Ex: Wolfgang Amadeus, Ludwig van"
              disabled={isSaving || isLoading}
              className="save-modal-composer-given"
            />
            <label htmlFor="exercise-composer-family">Nom du compositeur</label>
            <input
              id="exercise-composer-family"
              type="text"
              value={composerFamilyName}
              onChange={(e) => setComposerFamilyName(e.target.value)}
              placeholder="Ex: Mozart, Beethoven"
              disabled={isSaving || isLoading}
              className="save-modal-composer-family"
            />
            <small className="save-modal-hint">Suggestion automatique basée sur les exercices existants</small>
          </div>

          <div className="save-modal-field">
            <label htmlFor="exercise-work-title">
              Titre de l'Œuvre
            </label>
            <input
              id="exercise-work-title"
              type="text"
              value={workTitle}
              onChange={(e) => setWorkTitle(e.target.value)}
              placeholder="Ex: Symphonie 40, Prélude en Do majeur"
              disabled={isSaving || isLoading}
            />
            <small className="save-modal-hint">Suggestion automatique basée sur le titre YouTube</small>
            {!isEditMode && videoId && !isLoading && (
              <button
                type="button"
                className="save-modal-reanalyze"
                onClick={handleReanalyzeTitle}
                disabled={isSaving || isReanalyzing}
              >
                {isReanalyzing ? 'Ré-analyse…' : 'Re-analyser le titre (sans cache)'}
              </button>
            )}
            {!isLoading && workMatchStatus?.status === 'unverified' && (
              <div className="save-modal-work-unverified" role="status">
                Titre déduit du YouTube, non identifié dans la base. Vérifiez si besoin.
              </div>
            )}
            {!isLoading && workMatchStatus?.status === 'verified' && (workMatchStatus?.score ?? 0) > 0.8 && (
              <div className="save-modal-work-verified" role="status">
                Vérifié
              </div>
            )}
          </div>

          <div className="save-modal-field">
            <label htmlFor="exercise-movement">
              Mouvement <span className="save-modal-optional">(optionnel)</span>
            </label>
            {(() => {
              const c = composerDisplay.trim()
              const w = workTitle.trim()
              const searchQuery = [c, w].filter(Boolean).join(' ')
              if (searchQuery.length === 0) return null
              return (
                <div className="save-modal-movement-search-links">
                  Voir les mouvements :{' '}
                  <a href={`https://imslp.org/index.php?title=Special:Search&search=${encodeURIComponent(searchQuery)}`} target="_blank" rel="noopener noreferrer">IMSLP</a>
                  {' | '}
                  <a href={`https://en.wikipedia.org/wiki/Special:Search?search=${encodeURIComponent(searchQuery)}`} target="_blank" rel="noopener noreferrer">Wikipedia</a>
                  <small className="save-modal-hint save-modal-movement-links-hint">Ouvre la recherche IMSLP avec l&apos;œuvre et le compositeur.</small>
                  {imslpLinkFound !== null && (
                    <div className="save-modal-imslp-status" role="status">
                      Lien IMSLP : {imslpLinkFound ? (
                        imslpUrlUsed ? (
                          <a href={imslpUrlUsed} target="_blank" rel="noopener noreferrer">trouvé</a>
                        ) : (
                          'trouvé'
                        )
                      ) : (
                        'non trouvé'
                      )}
                    </div>
                  )}
                </div>
              )
            })()}
            {referenceMovementOptions && referenceMovementOptions.length > 0 ? (
              <>
                <select
                  id="exercise-movement"
                  value={referenceMovementOptions.includes(movementTitle) ? movementTitle : '__free__'}
                  onChange={(e) => setMovementTitle(e.target.value === '__free__' ? '' : e.target.value)}
                  disabled={isSaving || isLoading}
                  className="save-modal-movement-select"
                >
                  <option value="">— Choisir un mouvement —</option>
                  {referenceMovementOptions.map((mov) => (
                    <option key={mov} value={mov}>{mov}</option>
                  ))}
                  <option value="__free__">Autre (saisie libre)</option>
                </select>
                {(!movementTitle || !referenceMovementOptions.includes(movementTitle)) && (
                  <input
                    type="text"
                    value={movementTitle}
                    onChange={(e) => setMovementTitle(e.target.value)}
                    placeholder="ex. II. Andante, 2e mouvement"
                    disabled={isSaving || isLoading}
                    className="save-modal-movement-free-input"
                  />
                )}
              </>
            ) : (
              <input
                id="exercise-movement"
                type="text"
                value={movementTitle}
                onChange={(e) => setMovementTitle(e.target.value)}
                placeholder="ex. II. Andante, 2e mouvement"
                disabled={isSaving || isLoading}
              />
            )}
            <small className="save-modal-hint">Pour une œuvre en plusieurs mouvements (vidéo complète ou titre sans mouvement). Base de référence utilisée si l’œuvre est reconnue.</small>
          </div>

          <div className="save-modal-field">
            <label htmlFor="exercise-title">
              Titre de l'Exercice / Variante <span className="required">*</span>
            </label>
            <input
              id="exercise-title"
              type="text"
              value={exerciseTitle}
              onChange={(e) => setExerciseTitle(e.target.value)}
              placeholder="Ex: Exposition - Niveau 1, Analyse complète"
              required
              disabled={isSaving || isLoading}
              autoFocus
            />
            <small className="save-modal-hint">Nom spécifique de cet exercice (généré automatiquement, modifiable)</small>
          </div>

          <div className="save-modal-field">
            <label htmlFor="exercise-subtitle">
              Sous-titre / Section <span className="save-modal-optional">(optionnel)</span>
            </label>
            <input
              id="exercise-subtitle"
              type="text"
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              placeholder="ex: Exposition, Passage modulant, Coda..."
              disabled={isSaving || isLoading}
            />
            <small className="save-modal-hint">Permet de différencier plusieurs exercices sur la même œuvre. Affiché sous le titre.</small>
          </div>

          <div className="save-modal-field">
            <label htmlFor="exercise-difficulty">Niveau de difficulté</label>
            <select
              id="exercise-difficulty"
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
              disabled={isSaving || isLoading}
            >
              <option value="">Sélectionner...</option>
              <option value="facile">Facile</option>
              <option value="intermédiaire">Intermédiaire</option>
              <option value="difficile">Difficile</option>
              <option value="expert">Expert</option>
            </select>
            <small className="save-modal-hint">Suggestion automatique selon les accords et cadences (modifiable)</small>
          </div>

          <div className="save-modal-field">
            <label htmlFor="exercise-type">Type d&apos;exercice</label>
            <select
              id="exercise-type"
              value={exerciseType}
              onChange={(e) => {
                const v = e.target.value
                setExerciseType(v)
                if (v !== 'horizons') setMusicCategory('')
              }}
              disabled={isSaving || isLoading}
            >
              <option value="classical">Musique classique</option>
              <option value="horizons">Nouveaux Horizons (non classique)</option>
            </select>
            <small className="save-modal-hint">Nouveaux Horizons : film, JV, anime, variété, pop (section déblocable par les élèves)</small>
          </div>

          {exerciseType === 'horizons' && (
            <div className="save-modal-field">
              <label htmlFor="exercise-music-category">Style</label>
              <select
                id="exercise-music-category"
                value={musicCategory}
                onChange={(e) => setMusicCategory(e.target.value)}
                disabled={isSaving || isLoading}
              >
                <option value="">Sélectionner un style...</option>
                {HORIZONS_STYLE_ORDER.map((id) => (
                  <option key={id} value={id}>{HORIZONS_MUSIC_CATEGORIES[id].label}</option>
                ))}
              </select>
              <small className="save-modal-hint">Les tags Horizons et le style seront ajoutés automatiquement</small>
            </div>
          )}

          {exerciseType === 'horizons' && (
            <div className="save-modal-field">
              <label htmlFor="exercise-publication-year">
                Année de publication <span className="save-modal-optional">(optionnel)</span>
              </label>
              <input
                id="exercise-publication-year"
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={publicationYear}
                onChange={(e) => setPublicationYear(e.target.value)}
                placeholder={`Ex: 2001, 1994, ${new Date().getFullYear()}`}
                disabled={isSaving || isLoading}
              />
              <small className="save-modal-hint">Année de sortie de l'œuvre originale (film, jeu, anime, album…)</small>
            </div>
          )}

          {exerciseType === 'horizons' && musicCategory && getMusicalErasForCategory(musicCategory).length > 0 && (
            <div className="save-modal-field">
              <label htmlFor="exercise-musical-era">
                Ère musicale <span className="save-modal-optional">(auto-suggérée)</span>
              </label>
              <select
                id="exercise-musical-era"
                value={musicalEra}
                onChange={(e) => setMusicalEra(e.target.value)}
                disabled={isSaving || isLoading}
              >
                <option value="">— Aucune —</option>
                {getMusicalErasForCategory(musicCategory).map((era) => (
                  <option key={era.id} value={era.id}>{era.label}</option>
                ))}
              </select>
              <small className="save-modal-hint">Suggérée automatiquement depuis l'année. Tu peux la modifier.</small>
            </div>
          )}

          <div className="save-modal-field">
            <label>Formation (instrumentation)</label>
            <FormationInstrumentPicker
              masterList={exerciseType === 'horizons' ? HORIZONS_FORMATIONS : CLASSICAL_FORMATIONS}
              value={selectedFormations}
              onChange={setSelectedFormations}
              disabled={isSaving || isLoading}
              theme="modal"
              hint="Sélectionne un ou plusieurs (ex. Piano + Orchestre pour un concerto). Tape pour rechercher dans la liste complète."
            />
          </div>

          {exerciseType === 'classical' && (
            <div className="save-modal-field">
              <label htmlFor="exercise-genre">Genre (type d&apos;œuvre)</label>
              <select
                id="exercise-genre"
                value={genre}
                onChange={(e) => setGenre(e.target.value)}
                disabled={isSaving || isLoading}
              >
                <option value="">Non renseigné</option>
                {CLASSICAL_GENRES.map((g) => (
                  <option key={g.id} value={g.id}>{g.label}</option>
                ))}
              </select>
              <small className="save-modal-hint">Concerto, Trio, Symphonie, etc. (distinct de l&apos;instrumentation).</small>
            </div>
          )}

          <div className="save-modal-field save-modal-tags-field">
            <label>Tags de l&apos;exercice</label>
            <div className="save-modal-tags-chips">
              {manualTags.map((tag, index) => (
                <span key={`${tag}-${index}`} className="save-modal-tag-chip">
                  {tag}
                  <button
                    type="button"
                    className="save-modal-tag-chip-remove"
                    onClick={() => handleRemoveTag(index)}
                    disabled={isSaving || isLoading}
                    aria-label={`Retirer ${tag}`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="save-modal-tags-add">
              <input
                ref={tagInputRef}
                type="text"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onFocus={() => setShowTagSuggestions(true)}
                onBlur={() => setTimeout(() => setShowTagSuggestions(false), 200)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddTag()
                  }
                }}
                placeholder="Ajouter un tag..."
                disabled={isSaving || isLoading}
                className="save-modal-tag-input"
              />
              <button
                type="button"
                className="save-modal-tag-add-btn"
                onClick={() => handleAddTag()}
                disabled={isSaving || isLoading || !newTagInput.trim()}
              >
                Ajouter
              </button>
              {showTagSuggestions && tagSuggestions.length > 0 && (
                <div className="save-modal-tag-suggestions">
                  {tagSuggestions.filter(s => !manualTags.includes(s)).slice(0, 8).map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="save-modal-tag-suggestion"
                      onMouseDown={(e) => { e.preventDefault(); handleAddTag(s); }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="save-modal-tags-actions">
              <button
                type="button"
                className="save-modal-btn-reset-tags"
                onClick={handleResetAutoTags}
                disabled={isSaving || isLoading}
              >
                Réinitialiser aux tags automatiques
              </button>
            </div>
            <small className="save-modal-hint">Modifiez les tags pour corriger les erreurs ou ajouter des tags personnalisés.</small>
          </div>
        </div>

        <div className="save-modal-footer">
          {isEditMode && (
            <div className="save-modal-warning">
              ⚠️ Vous modifiez un exercice existant. Souhaitez-vous écraser l'original ou créer une copie ?
            </div>
          )}

          <div className="save-modal-field save-modal-privacy-field">
            <label htmlFor="exercise-privacy">Confidentialité</label>
            <select
              id="exercise-privacy"
              value={privacy}
              onChange={(e) => setPrivacy(e.target.value)}
              disabled={isSaving || isLoading}
            >
              <option value="private">Privé (visible uniquement par moi)</option>
              <option value="public">Public (visible par tous)</option>
            </select>
          </div>

          {isAdminTeacher && privacy === 'public' && (
            <div className="save-modal-field save-modal-privacy-field">
              <label className="save-modal-checkbox-row">
                <input
                  type="checkbox"
                  checked={publicPreviewTeaser}
                  onChange={(e) => setPublicPreviewTeaser(e.target.checked)}
                  disabled={isSaving || isLoading}
                />
                <span>Vitrine beta (lisible sans compte autorisé lorsque la beta privée est active)</span>
              </label>
            </div>
          )}

          <div className="save-modal-actions">
            <button
              type="button"
              className="save-modal-btn cancel-btn"
              onClick={handleClose}
              disabled={isSaving}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="save-modal-btn save-btn"
              disabled={!exerciseTitle.trim() || isSaving || isLoading}
            >
              {isSaving ? 'Sauvegarde...' : isEditMode ? 'Écraser' : 'Sauvegarder'}
            </button>
            {isEditMode && (
              <button
                type="button"
                className="save-modal-btn save-copy-btn"
                onClick={(e) => {
                  e.preventDefault()
                  const formationTags = selectedFormations.map(id => getFormationTag(id)).filter(Boolean)
                  const genreTag = genre ? getGenreTag(genre) : null
                  const tagsWithoutFormationGenre = manualTags.filter(t => !t.startsWith('Formation') && !t.startsWith('Genre'))
                  const autoTags = [...new Set([...formationTags, ...(genreTag ? [genreTag] : []), ...tagsWithoutFormationGenre])]
                  const yearValueCopy = exerciseType === 'horizons' && publicationYear ? Number(publicationYear) : null
                  const eraValueCopy = exerciseType === 'horizons' && musicalEra ? musicalEra : null
                  onSave({
                    composer: composerDisplay.trim() || null,
                    composerGivenName: composerGivenName.trim() || null,
                    composerFamilyName: composerFamilyName.trim() || null,
                    workTitle: workTitle.trim() || null,
                    movementTitle: movementTitle.trim() || null,
                    exerciseTitle: `${exerciseTitle.trim()} (Copie)`,
                    subtitle: subtitle.trim() || null,
                    difficulty: difficulty.trim() || null,
                    privacy: privacy,
                    autoTags,
                    section: exerciseType === 'horizons' ? 'horizons' : null,
                    musicCategory: exerciseType === 'horizons' && musicCategory ? musicCategory : null,
                    formation: selectedFormations.length > 0 ? selectedFormations : null,
                    genre: genre || null,
                    year: yearValueCopy,
                    musicalEra: eraValueCopy,
                    saveAsCopy: true,
                    ...(isAdminTeacher ? { publicPreview: publicPreviewTeaser } : {}),
                  })
                  resetForm()
                }}
                disabled={!exerciseTitle.trim() || isSaving || isLoading}
              >
                Sauvegarder copie
              </button>
            )}
          </div>
        </div>
        </form>
      </div>
    </div>
  )
}

export default SaveExerciseModal
